import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, RefreshCw } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

/**
 * 094 — «Los operadores pueden editar…» y el código de la estación.
 *
 * El check activa la edición desde `/operator/:id`; al activarlo el servidor
 * genera un código de seis dígitos que se enseña aquí —y sólo aquí: la
 * pantalla del operador nunca lo recibe—. «Código nuevo» rota el anterior en
 * el acto, que es lo que se quiere cuando el papel con el código se quedó en
 * el stand de ayer.
 */
export function OperatorEditToggle({ stationId }: { stationId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const stationQ = useQuery({
    queryKey: queryKeys.stations.detail(stationId),
    queryFn: () => eventsApi.getStation(stationId),
    enabled: stationId > 0,
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: queryKeys.stations.detail(stationId) });
    qc.invalidateQueries({ queryKey: queryKeys.stations.summary(stationId) });
  };
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => eventsApi.setOperatorEdit(stationId, enabled),
    onSuccess: invalidar,
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('stations.detail.operatorEdit.failed'), description: e.message }),
  });
  const rotate = useMutation({
    mutationFn: () => eventsApi.rotateOperatorCode(stationId),
    onSuccess: () => {
      invalidar();
      toast({ tone: 'success', title: t('stations.detail.operatorEdit.rotated') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('stations.detail.operatorEdit.failed'), description: e.message }),
  });

  const enabled = !!stationQ.data?.operatorEditEnabled;
  const code = stationQ.data?.operatorCode ?? null;

  async function copiar() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast({ tone: 'success', title: t('stations.detail.operatorEdit.copied') });
    } catch {
      toast({ tone: 'error', title: t('stations.detail.operatorEdit.failed') });
    }
  }

  return (
    <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3">
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
          checked={enabled}
          disabled={stationQ.isLoading || toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
        />
        <span>
          <span className="block text-sm font-medium text-[var(--color-fg-primary)]">
            {t('stations.detail.operatorEdit.label')}
          </span>
          <span className="block text-xs text-[var(--color-fg-muted)]">
            {t('stations.detail.operatorEdit.hint')}
          </span>
        </span>
      </label>

      {enabled && code ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--color-fg-muted)]">
            <KeyRound className="h-3.5 w-3.5" />
            {t('stations.detail.operatorEdit.code')}
          </span>
          <span className="rounded-md border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-1 font-mono text-lg font-bold tracking-[0.3em] tabular-nums">
            {code}
          </span>
          <Button variant="ghost" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" />} onClick={copiar}>
            {t('stations.detail.operatorEdit.copy')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            loading={rotate.isPending}
            onClick={() => rotate.mutate()}
          >
            {t('stations.detail.operatorEdit.rotate')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
